import React, { useContext } from 'react';
import { Box, Grid } from '@mui/material';
import ActivityBulletinComponent from '../my-restaurant/activity-bulletin.component';
import BannerAnnouncementComponent from '../my-restaurant/banner-announcement.component';
import PropagandaComponent from '../my-restaurant/propaganda.component';
import BulletinImageComponent from '../my-restaurant/bulletin-image.component';
import BulletinComponent from '../my-restaurant/bulletin.component';
import BackgroundImage from '../my-restaurant/background-image.component';
import SpecialDishes from '../my-restaurant/special-dishes.component';
import { FirstScreenBundledContext, FirstScreenBundledProvider } from '../my-restaurant/first-screen-bundled-context';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';
import { SubFunctionAuxiliaryHint } from './sub-function-auxiliary-hint.component';
import { FirstScreenPageSkeleton } from './first-screen-page-skeleton.component';
import './scss/first-screen.page.scss';

/**
 * 在 Provider 内根据 `bundledReady` 切换骨架与实卡；子卡片仍自行请求细节数据。
 * @param auxiliaryModeEnabled - 顶栏辅助模式
 */
function FirstScreenPageContent({ auxiliaryModeEnabled }: { auxiliaryModeEnabled: boolean }): JSX.Element {
    const bundledCtx = useContext(FirstScreenBundledContext);
    if (!bundledCtx?.bundledReady) {
        return <FirstScreenPageSkeleton auxiliaryModeEnabled={auxiliaryModeEnabled} />;
    }
    return (
        <Box className="first-screen-page" component="div">
            <Box className="first-screen-page__surface common-card-box">
                <SubFunctionAuxiliaryHint
                    enabled={auxiliaryModeEnabled}
                    i18nKey="first_screen_aux_page_intro"
                    className="first-screen-page__aux-intro"
                />
                <Grid container spacing={3} className="first-screen-page__grid">
                    {/** 首行：基础设置固定 480px，顶部文字公告占满剩余（对齐 Figma 1838:52035） */}
                    <Grid item xs={12}>
                        <Box
                            className="first-screen-page__top-row"
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: 3,
                                width: '100%',
                                alignItems: 'stretch',
                            }}
                        >
                            <Box
                                className="first-screen-page__basic-col"
                                sx={{
                                    width: { xs: '100%', md: '480px' },
                                    flexShrink: { md: 0 },
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <BackgroundImage auxiliaryModeEnabled={auxiliaryModeEnabled} />
                            </Box>
                            <Box
                                className="first-screen-page__banner-col"
                                sx={{ flex: { md: '1 1 0%' }, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column' }}
                            >
                                <BannerAnnouncementComponent auxiliaryModeEnabled={auxiliaryModeEnabled} />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <ActivityBulletinComponent auxiliaryModeEnabled={auxiliaryModeEnabled} />
                    </Grid>
                    <Grid item xs={12}>
                        <BulletinImageComponent auxiliaryModeEnabled={auxiliaryModeEnabled} />
                    </Grid>
                    <Grid item xs={12}>
                        <SpecialDishes auxiliaryModeEnabled={auxiliaryModeEnabled} />
                    </Grid>
                    {/* 文字就餐指南 + 社媒宣传链接：Figma 1838:52035 底部为同一行左右两列 */}
                    <Grid item xs={12}>
                        <Grid container spacing={3} className="first-screen-page__guide-propaganda-row">
                            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                    <BulletinComponent auxiliaryModeEnabled={auxiliaryModeEnabled} />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                    <PropagandaComponent auxiliaryModeEnabled={auxiliaryModeEnabled} />
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

/**
 * 云 RBP「首页与营销设置」内容区：餐厅背景、公告、活动图等模块栅格排列（顶栏 Tab 已展示模块名，此处不再重复页标题）。
 * 隧道与当前餐厅校验在父级「子功能管理」壳或独立 `home-marketing` 路由的 `HookDecorator` 中完成，本类不再挂 `CurrentRestaurantDecorator`。
 */
export default class FirstScreenPage extends React.Component<Props, State> {
    render() {
        return (
            <RbpCloudSubFunctionAuxiliaryModeContext.Consumer>
                {({ auxiliaryModeEnabled }) => (
                    <FirstScreenBundledProvider>
                        <FirstScreenPageContent auxiliaryModeEnabled={auxiliaryModeEnabled} />
                    </FirstScreenBundledProvider>
                )}
            </RbpCloudSubFunctionAuxiliaryModeContext.Consumer>
        );
    }
}

// *********************
// Type
// *********************
/** 首屏设置页无父组件传入 props */
type Props = Record<string, never>;
/** 首屏设置页无本地 state（各子卡片自行维护） */
type State = Record<string, never>;
